import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, navigate } from "raviger";
import axios from "axios";
import { Button, Card, Col, Form, FormControl, InputGroup, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft, faEnvelope, faInfoCircle, faMinusSquare, faUpload, faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import {
  faCircleE, faCircleI, faCircleO, faCircleT, faCircleV, faPencil, faUserUnlock
} from '@fortawesome/pro-solid-svg-icons';
import { Field, Formik } from "formik";
import * as Yup from 'yup';
import Moment from 'react-moment';
import Header from "../components/Header";
import { AuthContext } from "./AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import { TextInput } from '../components/Form';
import LoadingLink from '../components/LoadingLink';
import ActionsDropdown from '../components/ActionsDropdown';

function UserManagement({ organization }) {

  const { dispatch, state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({users: [], isFetching: false});
  const [filteredData, setFilteredData] = useState({users: [], isFetching: false});

  const [userToDelete, setUserToDelete] = useState({id: 0, first_name: '', last_name: ''});
  const [showUserConfirm, setShowUserConfirm] = useState(false);
  const handleUserClose = () => setShowUserConfirm(false);

  const [userToReset, setUserToReset] = useState({id: 0, first_name: '', last_name: ''});
  const [showUserResetConfirm, setShowUserResetConfirm] = useState(false);
  const handleUserResetClose = () => setShowUserResetConfirm(false);

  const [newEmailData, setNewEmailData] = useState({id:null, new_email:'', org_slug:organization});
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const handleChangeEmailClose = () => setShowChangeEmail(false);
  const newEmailRef = useRef(null);

  const [file, setFile] = useState({users: [], isFetching: false});
  const [showUploadCSV, setShowUploadCSV] = useState(false);
  const handleUploadCSVClose = () => setShowUploadCSV(false);

  const [timer, setTimer] = useState(null);

  const handleRemoveUserSubmit = async () => {
    await axios.delete('/accounts/api/user/' + userToDelete.id + '/?organization=' + state.organization.id)
    .then(response => {
      setData(prevState => ({ ...prevState, "users":data.users.filter(user => user.id !== userToDelete.id) }));
      setFilteredData(prevState => ({ ...prevState, "users":data.users.filter(user => user.id !== userToDelete.id) }));
      setUserToDelete({id: 0, first_name: '', last_name: ''});
      handleUserClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleResetUserSubmit = async () => {
    await axios.patch('/accounts/api/user/' + userToReset.id + '/', {'reset_password':true, 'organization': state.organization.id})
    .then(response => {
      setUserToReset({id: 0, first_name: '', last_name: ''});
      handleUserResetClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleUploadCSV = async () => {
    const formData = new FormData();
    formData.append('user_csv', file);

    await axios.post('/accounts/api/user/upload_csv/?organization=' + organization, formData)
    .then(response => {
      setData(prevState => ({ ...prevState, "users":data.users.concat(response.data)}));
      setFilteredData(prevState => ({ ...prevState, "users":filteredData.users.concat(response.data)}));
      handleUploadCSVClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  // Use a delay for quick search
  function changeDelay(searchTerm) {
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
    setTimer(
      setTimeout(() => {
        handleSearch(searchTerm);
      }, 1000)
    );
  }

  // Update searchTerm when field input changes.
  const handleSearch = (searchTerm) => {
    if (searchTerm) {
      setFilteredData(prevState => ({ ...prevState,
        "users":data.users.filter(user => (user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  user.cell_phone.toLowerCase().includes(searchTerm.toLowerCase()))
      ) }));
    }
    else {
      setFilteredData(prevState => ({ ...prevState, "users":data.users }));
    }
  };

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    if (state.organization.id) {
      const fetchUserData = async () => {

        setData({users: [], isFetching: true});
        axios.get('/accounts/api/user/?organization=' + state.organization.id, {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            setData({users: response.data, isFetching: false});
            setFilteredData({users: response.data, isFetching: false});
          }
        })
        .catch(error => {
          if (!unmounted) {
            setData({users: [], isFetching: false});
            setShowSystemError(true);
          }
        });

        // Cleanup.
        return () => {
          unmounted = true;
          source.cancel();
        };
      }

      fetchUserData();
    }

  }, [state.organization.id]);

  return (
    <>
    {state.user.is_superuser || state.user.user_perms ?
    <span className="mt-4 ms-auto me-auto" style={{width:"80%", maxWidth:"80%"}}>
    <Header>
      <span style={{cursor:'pointer'}} onClick={() => navigate("/" + organization + '/manage/organization')} className="me-2"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="sm" inverse /></span>
      {state.organization.name} - Manage Users
      <OverlayTrigger
        key={"add-user"}
        placement="bottom"
        overlay={
          <Tooltip id={`tooltip-add-user`}>
            Create new user
          </Tooltip>
        }
      >
        <Link href={"/" + organization + "/accounts/user/new"}><FontAwesomeIcon icon={faUserPlus} size="sm" className="ms-2 fa-move-up" inverse /></Link>
      </OverlayTrigger>
    </Header>
    <InputGroup className="mb-3">
      <FormControl
        type="text"
        placeholder="Quick Search..."
        name="searchTerm"
        onChange={(event) => {
          changeDelay(event.target.value);
        }}
      />
    </InputGroup>
    <Row className="flex-nowrap">
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"0px"}}>
          Last Name
      </Col>
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"1px"}}>
          First Name
      </Col>
      <Col style={{minWidth:"300px", maxWidth:"300px"}}>
          Email
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px"}}>
          Phone
      </Col>
      <Col style={{paddingRight:"0px", minWidth:"100px", maxWidth:"100px"}}>
          Operational
      </Col>
      <Col style={{paddingRight:"0px", minWidth:"100px", maxWidth:"100px"}}>
          Training
      </Col>
      <Col style={{minWidth:"75px"}}>
          Perms
      </Col>
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
          Actions
      </Col>
    </Row>
    {filteredData.users.filter(user => !user.is_superuser).map(user => (
      <Card key={user.id} className="rounded mb-1 ms-2" style={{height:"32px"}}>
        <div className="row no-gutters flex-nowrap me-0">
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.last_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.first_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"300px", maxWidth:"300px", backgroundColor:"#615e5e"}}>
            {user.email}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}}>
            {user.display_phone}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", backgroundColor:"#615e5e"}}>
            {user.all_operational ? "All" : Object.keys(user.incident_objects.filter(inc => !inc.training)).length === 0 ? "None" : <OverlayTrigger
              key={"active-incidents"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-active-incidents`}>
                  {Object.entries(user.incident_objects.filter(inc => !inc.training)).map(([inc_slug, incident]) => (
                    <span key={inc_slug}>
                      {incident.name}
                      <br />
                    </span>
                  ))}
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
            </OverlayTrigger>}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", backgroundColor:"#615e5e"}}>
            {user.all_training ? "All" : Object.keys(user.incident_objects.filter(inc => inc.training)).length === 0 ? "None" : <OverlayTrigger
              key={"active-incidents"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-active-incidents`}>
                  {Object.entries(user.incident_objects.filter(inc => inc.training)).map(([inc_slug, incident]) => (
                    <span key={inc_slug}>
                      {incident.name}
                      <br />
                    </span>
                  ))}
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
            </OverlayTrigger>}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"75px", backgroundColor:"#615e5e"}}>
            {user.user_perms ? <OverlayTrigger
              key={"org-perms"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-org-perms`}>
                  User is an Organization Admin
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faCircleO} size="lg" className="ms-1" />
            </OverlayTrigger> : ""}
            {user.incident_perms ? <OverlayTrigger
              key={"incident-perms"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-incident-perms`}>
                  User is an Incident Admin
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faCircleI} size="lg" className="ms-1" />
            </OverlayTrigger> : ""}
            {user.vet_perms ? <OverlayTrigger
              key={"vet-perms"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-vet-perms`}>
                  User has veterinary permissions
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faCircleV} size="lg" className="ms-1" />
            </OverlayTrigger> : ""}
          </Col>
          <Col className="border" style={{height:"32px", paddingRight:"4px", paddingTop:"2px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
            <ActionsDropdown variant="secondary" title={"Actions"} className="float-end" style={{height:"26px", paddingTop:"0px"}}>
							<LoadingLink
								href={"/" + organization + "/accounts/user/edit/" + user.id}
								className="d-block"
								isLoading={false}
								label="Update User"
								>
								<FontAwesomeIcon icon={faPencil} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
								onClick={() => {setNewEmailData({id:user.id, new_email:'', org_slug:organization});setShowChangeEmail(true);setTimeout(()=>{newEmailRef.current && newEmailRef.current.focus()}, 1);}}
								className="d-block"
								isLoading={false}
								label="Change User Email Address"
								>
								<FontAwesomeIcon icon={faEnvelope} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
								onClick={() => {setUserToDelete({id:user.id, first_name: user.first_name, last_name: user.last_name});setShowUserConfirm(true);}}
								className="d-block"
								isLoading={false}
								label="Remove User"
								>
								<FontAwesomeIcon icon={faMinusSquare} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
								onClick={() => {setUserToReset({id:user.id, first_name: user.first_name, last_name: user.last_name});setShowUserResetConfirm(true);}}
								className="d-block"
								isLoading={false}
								label="Reset User Password"
								>
								<FontAwesomeIcon icon={faUserUnlock} className="fas" inverse />
							</LoadingLink>
						</ActionsDropdown>
          </Col>
        </div>
      </Card>
    ))}
    <br/>
    <Row className="flex-nowrap" style={{marginLeft:"-15px"}}>
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"15px", marginRight:"0px", paddingLeft:"0px", paddingRight:"0px"}}>
          Admin Last Name
      </Col>
      <Col style={{minWidth:"125px", maxWidth:"125px", marginLeft:"3px", marginRight:"0px", paddingLeft:"0px", paddingRight:"0px"}}>
          Admin First Name
      </Col>
      <Col style={{minWidth:"300px", maxWidth:"300px", paddingLeft:"0px"}}>
          Admin Email
      </Col>
    </Row>
    {filteredData.users.filter(user => user.is_superuser).map(user => (
      <Card key={user.id} className="rounded mb-1 ms-2" style={{height:"32px", maxWidth:"540px"}}>
        <div className="row no-gutters flex-nowrap">
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.last_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"125px", maxWidth:"125px", backgroundColor:"#615e5e"}}>
            {user.first_name}
          </Col>
          <Col className="border" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", minWidth:"300px", maxWidth:"300px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
            {user.email}
          </Col>
        </div>
      </Card>
    ))}
    </span> : ""}
    <Modal show={showUserConfirm} onHide={handleUserClose}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm User Removal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Are you sure you would like to remove user {userToDelete.first_name} {userToDelete.last_name}?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleRemoveUserSubmit}>Yes</Button>
        <Button variant="secondary" onClick={handleUserClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showUserResetConfirm} onHide={handleUserResetClose}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm User Password Reset</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Are you sure you would like to initialize a password reset for user {userToReset.first_name} {userToReset.last_name}?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleResetUserSubmit}>Yes</Button>
        <Button variant="secondary" onClick={handleUserResetClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showUploadCSV} onHide={handleUploadCSVClose}>
      <Modal.Header closeButton>
        <Modal.Title>Upload User CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="formFile" className="mt-3 mb-3">
          <Form.Control type="file" onChange={(event) => {setFile(event.target.files[0]) }} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleUploadCSV}>Upload</Button>
        <Button variant="secondary" onClick={handleUploadCSVClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
    <Formik
      initialValues={newEmailData}
      enableReinitialize={true}
      validationSchema={Yup.object({
        id: Yup.string().required(),
        new_email: Yup.string().required('Please enter a valid email address.')
          .max(50, 'Must be 50 characters or less')
          .email('Please enter a valid email address.'),
      })}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        // Send verification email.
        axios.get('/accounts/api/user/' + values.id + '/email_verification/', {params:values})
        .then(response => {
          resetForm();
          setNewEmailData({id:null, new_email:'', org_slug:organization});
          setShowChangeEmail(false);
        })
        .catch(error => {
          setShowSystemError(true);
        });
      }}
    >
    {formikProps => (
      <Modal show={showChangeEmail} onHide={handleChangeEmailClose} aria-modal="true">
        <Modal.Header closeButton>
          <Modal.Title>Change Email Address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mt-3">
            <TextInput
              rows={5}
              label="New Email Address"
              name="new_email"
              id="new_email"
              xs="12"
              ref={newEmailRef}
            />
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {formikProps.submitForm();}}>Send Verification Email</Button>
          <Button variant="secondary" onClick={handleChangeEmailClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    )}
    </Formik>
    </>
  )
}

export default UserManagement
